import {
  AutorenewIcon,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  EditIcon,
  KeyboardArrowRightIcon,
  makeStyles,
  PlaceIcon,
  Theme,
  XDatePicker as DatePicker,
} from '@freighthub/silhouette-core'
import { isSeaPort, PortOrAddress, portOrAddressToString } from 'features/Directory'
import { TransportModeIcon } from 'features/TransportPlan'
import { find } from 'lodash-es'
import { toJS } from 'mobx'
import { FC, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { fromQuoteSheet } from '../../fromMobx'
import { useQuoteService } from '../../QuoteService'
import { IQuoteSheet } from '../QuoteSheetModel'
import { usePdfDownloadMode } from '../usePdfDownloadMode'

const useStyles = makeStyles<Theme>((theme) => ({
  originDestinationContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 2, 1),
    display: 'flex',
  },
  rightArrowIcon: {
    margin: theme.spacing(0, 1, 0.5),
  },
  editIcon: {
    marginRight: theme.spacing(1),
  },
  transportIcon: {
    fontSize: theme.typography.h6.fontSize,
  },
  icon: {
    width: 18,
    height: 18,
    fontSize: theme.typography.h6.fontSize,
  },
}))

interface OriginDestinationSectionProps {
  quoteSheet: IQuoteSheet
  onEdit?: () => void
  editable?: boolean
}

interface TransportIconProps {
  portOrAddress: PortOrAddress
  quoteSheet: IQuoteSheet
}

export const OriginDestinationSection: FC<OriginDestinationSectionProps> = ({ quoteSheet, onEdit, editable }) => {
  const { isPdfDownloadMode } = usePdfDownloadMode()
  const [crdDialogOpen, setCrdDialogOpen] = useState(false)
  const [crd, setCrd] = useState<Date | null>(null)
  const { origin, destination } = quoteSheet.request.journey
  const { translate } = useI18n()
  const classes = useStyles()
  const quoteService = useQuoteService()

  const handleCRDDialogClose = () => {
    setCrdDialogOpen(false)
    setCrd(null)
  }

  const handleCRDDialogOpen = () => {
    setCrdDialogOpen(true)
  }

  const onRefreshSearch = () => {
    if (!crd) {
      return
    }

    const request = {
      ...fromQuoteSheet(toJS(quoteSheet)),
      cargoReadyAt: crd,
    }

    quoteService.search(request)
    setCrdDialogOpen(false)
  }

  return (
    <div className={classes.originDestinationContainer}>
      <Box lineHeight={1.3} paddingRight={2} display="flex" alignItems="center">
        <TransportIcon portOrAddress={origin} quoteSheet={quoteSheet} />
        <div>{portOrAddressToString(origin)}</div>
        <KeyboardArrowRightIcon className={classes.rightArrowIcon} />
        <TransportIcon portOrAddress={destination} quoteSheet={quoteSheet} />
        <div>{portOrAddressToString(destination)}</div>
      </Box>
      {editable && !isPdfDownloadMode && (
        <Box flexShrink={0}>
          <Button onClick={onEdit}>
            <EditIcon className={classes.editIcon} />
            {translate('quotesheet_header-edit')}
          </Button>
        </Box>
      )}

      <Button onClick={handleCRDDialogOpen}>
        <AutorenewIcon />
      </Button>

      <Dialog open={crdDialogOpen} onClose={handleCRDDialogClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">{translate('refresh-quote-crd-dialog-title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate('refresh-quote-crd-dialog-content')}</DialogContentText>
          <DatePicker
            label={translate('quote_request-cargo-cargo_ready-title')}
            value={crd}
            onChange={setCrd}
            disablePast
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCRDDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={onRefreshSearch} color="primary">
            Requote
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const TransportIcon: FC<TransportIconProps> = ({ portOrAddress, quoteSheet }) => {
  const classes = useStyles()
  let Icon = <PlaceIcon className={classes.icon} />
  if (isSeaPort(portOrAddress)) {
    if (find(quoteSheet.request.modes, (m) => m === 'air')) {
      Icon = <TransportModeIcon transportMode={quoteSheet.request.modes[0]} className={classes.transportIcon} />
    } else {
      Icon = <TransportModeIcon transportMode="sea" className={classes.transportIcon} />
    }
  }

  return (
    <Box display="inline-block" marginRight={0.5} lineHeight={1}>
      {Icon}
    </Box>
  )
}
